import type { RectModel } from '@/types/documents.types';
import { DEFAULT_SUBSTITUTE_FONT, type SubstituteFont } from '@/utils/substituteFont';

/*
 * Where and how big the anonymized preview sets a substitute.
 *
 * Where the backend's export (`services/pdf/export.py`, `_insert_substitute`) sets it — the first
 * character's baseline origin, at that character's font size and horizontal scale — read here from
 * the page's pdf.js text items instead of from PDFium. Like the export, the preview never changes
 * the font size: a substitute too wide for its room is condensed horizontally, a narrower one
 * widened a little (`fitSubstitute`). The export sets it in the document's own embedded font, or in
 * DejaVu Sans where that font lacks a glyph (`FindingModel.substitute_font_fallback`); the preview
 * uses the same embedded font as pdf.js loaded it (`utils/substituteFont`) for the former and
 * DejaVu Sans for the latter. A finding that wraps gets its substitute shared out over its lines
 * (`utils/substituteLines`), as the export does.
 */

/** Share by which a substitute may overshoot its run before it is condensed: the export's `WIDTH_TOLERANCE`. */
export const WIDTH_TOLERANCE = 0.01;

/**
 * Side bearings a finding's tight glyph box leaves out of its run's advance width, in ems.
 *
 * The export measures the room against the advance (loose) boxes of the run; the finding rectangles
 * are the union of the tight glyph boxes, a little narrower at both ends. A tenth of an em is about
 * the two bearings of common glyphs together.
 */
export const SIDE_BEARINGS_EM = 0.1;

/** How far a text item may reach past a finding rectangle and still be the run it replaces, in ems. */
const SAME_RUN_EM = 0.3;

/** How far below a tight glyph box a baseline may sit (round glyphs overshoot it), in ems. */
const BASELINE_OVERSHOOT_EM = 0.1;

/**
 * A gap this many ems wide between two runs of one line parts two table columns; a word space,
 * even stretched by justification, is a fraction of an em. The backend's earlier export split its
 * runs at the same width.
 */
export const COLUMN_GAP_EM = 1.5;

/** A run whose baseline drifts by more than this share of its font size is not horizontal. */
const MAX_BASELINE_DRIFT_EM = 0.05;

/*
 * Without a text item, the rectangle alone: a tight box of capitals, ascenders and descenders is
 * about 0.85 em tall, and its baseline sits about 0.12 em above its bottom edge.
 */
const FALLBACK_BOX_EM = 0.85;
const FALLBACK_DESCENT_EM = 0.12;

/*
 * A font's ascent and descent in ems, where pdf.js reports none. Common text faces sit near these,
 * as DejaVu Sans (0.93 / 0.24) and Arial (0.91 / 0.21) do.
 */
const DEFAULT_ASCENT_EM = 0.9;
const DEFAULT_DESCENT_EM = 0.22;

/**
 * How far a caron on a capital (Č, Ř, Š, Ž) reaches above the font's declared ascent, in ems:
 * Times puts its ascent at 0.891 em and the caron of "Ř" a little higher. Covers take it too.
 */
const ACCENT_OVERSHOOT_EM = 0.06;

/**
 * How far above a finding rectangle the glyphs may still reach, as a share of its height, when no
 * run tells. PDFium's tight boxes stop at the cap height and leave accents out (measured: tops at
 * 0.73 em against a 0.91 em ascent), and Czech has an accent on almost every other capital.
 */
const FALLBACK_ACCENT_SHARE = 0.25;

/** One horizontal text run of the page, in PDF points from the page's top-left corner. */
export interface TextRun {
  /** The run's text as pdf.js reports it. */
  text: string;
  /** Advance box of the run: where its first glyph starts and its last glyph ends. */
  x0: number;
  x1: number;
  baseline: number;
  fontSize: number;
  /** Width of an em over its height: 1 for plain text, less for a condensed run (`Tz`). */
  horizontalScale: number;
  /** How far the font reaches above and below the baseline, in ems (both positive). */
  ascent: number;
  descent: number;
  /** pdf.js's handle of the run's font, to look its PDF name up by (`commonObjs`). */
  fontName?: string;
  /** pdf.js's guess of the font's class: `serif`, `sans-serif` or `monospace`. */
  genericFamily?: string;
  /** The font to set the run's substitutes in, once the PDF's font name is known. */
  font?: SubstituteFont;
}

/** Where to set one substitute and at what size, in PDF points from the page's top-left corner. */
export interface SubstitutePlacement {
  /** Left end of the substitute's baseline. */
  x: number;
  baseline: number;
  /** Font size and horizontal scale after fitting the room (`fitSubstitute`). */
  fontSize: number;
  horizontalScale: number;
  font: SubstituteFont;
}

/** Width of `text` set in `font` at `fontSize`, in the unit of the size. */
export type MeasureText = (text: string, fontSize: number, font: SubstituteFont) => number;

/** The part of a pdf.js `TextItem` read here; `TextMarkedContent` entries carry no `str`. */
interface TextItemLike {
  str: string;
  /** Text rendering matrix in PDF user space: `[a, b, c, d, e, f]`, origin on the baseline. */
  transform: number[];
  /** Advance width of the item in PDF user space units. */
  width: number;
  fontName?: string;
}

/** The part of pdf.js's per-font `TextStyle` read here, keyed by a text item's `fontName`. */
export interface TextStyleLike {
  ascent: number;
  descent: number;
  fontFamily?: string;
}

/** The part of a pdf.js `PageViewport` (at scale 1) read here. */
export interface ViewportLike {
  convertToViewportPoint(x: number, y: number): number[];
}

const isTextItem = (item: unknown): item is TextItemLike =>
  typeof item === 'object' &&
  item !== null &&
  typeof (item as TextItemLike).str === 'string' &&
  Array.isArray((item as TextItemLike).transform) &&
  typeof (item as TextItemLike).width === 'number';

/**
 * The horizontal text runs of a page, from its pdf.js text content.
 *
 * `viewport` is the page's viewport at scale 1, which puts the runs in the coordinate system of the
 * finding rectangles: PDF points, origin top-left, page rotation applied (ADR 0006). Runs that are
 * rotated against the page or set vertically are left out; a finding on one keeps the fallback.
 */
export const textRuns = (
  items: readonly unknown[],
  viewport: ViewportLike,
  styles: Readonly<Record<string, TextStyleLike>> = {}
): TextRun[] => {
  const runs: TextRun[] = [];
  for (const item of items) {
    if (!isTextItem(item) || item.str.trim() === '') continue;
    const [a, b, c, d, e, f] = item.transform;
    const fontSize = Math.hypot(c, d);
    const emWidth = Math.hypot(a, b);
    if (!(fontSize > 0) || !(emWidth > 0)) continue;
    const [x0, baseline] = viewport.convertToViewportPoint(e, f);
    // The far end of the run, `width` units along its own direction from the origin.
    const [x1, endBaseline] = viewport.convertToViewportPoint(
      e + (a / emWidth) * item.width,
      f + (b / emWidth) * item.width
    );
    if (x1 <= x0 || Math.abs(endBaseline - baseline) > fontSize * MAX_BASELINE_DRIFT_EM) continue;
    const style = item.fontName ? styles[item.fontName] : undefined;
    runs.push({
      text: item.str,
      x0,
      x1,
      baseline,
      fontSize,
      horizontalScale: emWidth / fontSize,
      // pdf.js gives the descent as a negative number, and zero for a font it knows no metrics of.
      ascent: style && style.ascent > 0 ? style.ascent : DEFAULT_ASCENT_EM,
      descent: style && style.descent < 0 ? -style.descent : DEFAULT_DESCENT_EM,
      fontName: item.fontName,
      genericFamily: style?.fontFamily,
    });
  }
  return runs;
};

/**
 * The run a finding rectangle (one line of a finding) was cut from, or `null` when none matches.
 *
 * Glyphs stand on their baseline, so the baseline lies inside the tight box or at its bottom edge,
 * while the baselines of the lines above and below lie well outside it. Of the runs that pass, the
 * one covering most of the rectangle wins.
 */
export const runForRect = (rect: RectModel, runs: readonly TextRun[]): TextRun | null => {
  let best: TextRun | null = null;
  let bestOverlap = 0;
  for (const run of runs) {
    if (!onLineOf(rect, run)) continue;
    const overlap = Math.min(rect.x1, run.x1) - Math.max(rect.x0, run.x0);
    if (overlap > bestOverlap) {
      best = run;
      bestOverlap = overlap;
    }
  }
  return best;
};

/** True when the run stands on the line a finding rectangle was cut from (see `runForRect`). */
const onLineOf = (rect: RectModel, run: TextRun): boolean =>
  run.baseline > rect.y0 && run.baseline <= rect.y1 + run.fontSize * BASELINE_OVERSHOOT_EM;

/** Every run of a finding rectangle's line that the rectangle overlaps, left to right. */
export const runsForRect = (rect: RectModel, runs: readonly TextRun[]): TextRun[] =>
  runs
    .filter((run) => onLineOf(rect, run) && Math.min(rect.x1, run.x1) - Math.max(rect.x0, run.x0) > 0)
    .sort((a, b) => a.x0 - b.x0);

/** One column's share of a finding rectangle, with the runs it covers there. */
export interface LineSegment {
  rect: RectModel;
  runs: TextRun[];
}

/**
 * Split a finding rectangle at the column gaps between its runs.
 *
 * A selection across a table row is one line in the text layer — the backend joins a row's cells
 * with a single space — so it is one rectangle from the first cell to the last. Set as one string
 * from the left edge, its substitute would pull every column into the first. Split where two runs
 * lie `COLUMN_GAP_EM` or more apart, each column gets its own part at its own place. Without runs
 * (no text content yet) the rectangle stays whole.
 */
export const columnSegments = (rect: RectModel, lineRuns: readonly TextRun[]): LineSegment[] => {
  if (lineRuns.length <= 1) return [{ rect, runs: [...lineRuns] }];
  const groups: TextRun[][] = [[lineRuns[0]]];
  for (const run of lineRuns.slice(1)) {
    const group = groups[groups.length - 1];
    const previous = group[group.length - 1];
    if (run.x0 - previous.x1 >= COLUMN_GAP_EM * Math.max(run.fontSize, previous.fontSize)) groups.push([run]);
    else group.push(run);
  }
  return groups.map((group) => ({
    rect: {
      x0: Math.max(rect.x0, group[0].x0),
      x1: Math.min(rect.x1, group[group.length - 1].x1),
      y0: rect.y0,
      y1: rect.y1,
    },
    runs: group,
  }));
};

/**
 * The area to cover over one finding rectangle so none of the original glyphs shows.
 *
 * The rectangles are PDFium's tight glyph boxes, which stop at the cap height: an accent, the dot
 * of an "i" or a tall ascender reaches above them. With the run, the cover spans the font's whole
 * height, ascent (plus the capitals' carons) to descent, across the rectangle's width; without it, the rectangle grows upwards
 * by the share accents take.
 */
export const coverRect = (rect: RectModel, run: TextRun | null): RectModel => {
  if (!run) {
    return { ...rect, y0: rect.y0 - Math.max(rect.y1 - rect.y0, 0) * FALLBACK_ACCENT_SHARE };
  }
  return {
    x0: rect.x0,
    x1: rect.x1,
    y0: Math.min(rect.y0, run.baseline - (run.ascent + ACCENT_OVERSHOOT_EM) * run.fontSize),
    y1: Math.max(rect.y1, run.baseline + run.descent * run.fontSize),
  };
};

/** The font size and horizontal scale a substitute ends up with. */
export interface SubstituteFit {
  fontSize: number;
  horizontalScale: number;
}

/** How far a narrower substitute is widened towards the original's width: the export's `MAX_HORIZONTAL_SCALE`. */
export const MAX_WIDEN = 1.1;

/**
 * Fit a substitute of `natural` width into `available` room, as the export's `_fitted` does.
 *
 * Within the tolerance nothing changes. Wider, the text is condensed as far as the room needs and
 * keeps its font size: the backend picks substitutes that fit, so this only bites when none did.
 * Narrower, it is widened towards the room by at most `MAX_WIDEN`, so the text after it does not
 * stand apart.
 */
export const fitSubstitute = (
  natural: number,
  available: number,
  fontSize: number,
  horizontalScale: number
): SubstituteFit => {
  if (!(available > 0) || !(natural > 0)) return { fontSize, horizontalScale };
  if (natural > available * (1 + WIDTH_TOLERANCE))
    return { fontSize, horizontalScale: (horizontalScale * available) / natural };
  if (natural < available * (1 - WIDTH_TOLERANCE))
    return { fontSize, horizontalScale: horizontalScale * Math.min(available / natural, MAX_WIDEN) };
  return { fontSize, horizontalScale };
};

/** True when the rectangle is the whole run, so the run's own advance box is the room to fill. */
const coversRun = (rect: RectModel, run: TextRun): boolean =>
  Math.abs(run.x0 - rect.x0) <= SAME_RUN_EM * run.fontSize && Math.abs(run.x1 - rect.x1) <= SAME_RUN_EM * run.fontSize;

/**
 * Place a substitute, or one line's part of it, on one line of its finding.
 *
 * With the run the line was cut from, the substitute takes the run's baseline, font size,
 * horizontal scale and font; without one (no text content, a rotated run) the rectangle's height
 * stands in, in plain sans-serif.
 * The room is the run's advance width when the finding is the whole run, otherwise the tight box
 * widened by the side bearings; the text starts where the first glyph's advance box starts.
 */
export const placeSubstitute = (
  text: string,
  rect: RectModel,
  run: TextRun | null,
  measure: MeasureText
): SubstitutePlacement => {
  const height = Math.max(rect.y1 - rect.y0, 0);
  const fontSize = run?.fontSize ?? height / FALLBACK_BOX_EM;
  const horizontalScale = run?.horizontalScale ?? 1;
  const baseline = run?.baseline ?? rect.y1 - fontSize * FALLBACK_DESCENT_EM;
  const whole = run !== null && coversRun(rect, run);
  const x = whole ? run.x0 : rect.x0 - (SIDE_BEARINGS_EM / 2) * fontSize;
  const available = whole ? run.x1 - run.x0 : rect.x1 - rect.x0 + SIDE_BEARINGS_EM * fontSize;
  const font = run?.font ?? DEFAULT_SUBSTITUTE_FONT;
  const natural = measure(text, fontSize, font) * horizontalScale;
  return { x, baseline, font, ...fitSubstitute(natural, available, fontSize, horizontalScale) };
};
